import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { addJobForSnooze, updateSnoozeState } from "../utils/commonSnoozeUtils";

const stringUtil = require("../../../utils/string/stringUtil");
const constants = require("../../../utils/domain/constants");
const dateUtil = require("../../../utils/dateUtil/dateUtil");
const localizationUtils = require("../../../utils/domain/localizationUtils");
const ItemSnoozeViewContext = createContext();

const translator = require("../../../utils/translator/translator");
const viewUtil = require("../../../utils/view/viewUtil");
const stateManager = require("../../../utils/state/stateManager");
const navigationPageAction = require("../../../utils/state/actions/views/navigationPage/navigationPageAction");

const useStore = ({ menus, existingSnoozes, tags }) => {
     const [searchText, setSearchText] = useState("");
     const [snoozeItemTags, setSnoozeItemTags] = useState([]);
     const [openedTagId, setOpenedTagId] = useState(null);

     const [venueOptions] = useState(() => {
          const venues = localizationUtils.getApplicableVenues();
          return venues.map((venue) => ({ label: venue.code, value: venue.code }));
     });
     const [menuOptions] = useState(() => {
          return menus.map((menu) => ({ label: menu.name, value: menu.id }));
     });

     const checkIsDataUpdatedReverseCount = useRef(-1);

     const filteredSnoozeItemTags = useMemo(() => {
          return snoozeItemTags
               .filter((sTag) => {
                    if (searchText !== "" && !sTag.primaryId.toLowerCase().includes(searchText.trim().toLowerCase())) return false;
                    return true;
               })
               .sort((a, b) => a.primaryId.toLowerCase().localeCompare(b.primaryId.toLowerCase()));
     }, [snoozeItemTags, searchText]);

     const openTagModal = (primaryId) => {
          setOpenedTagId(primaryId);
     };

     const closeTagModal = () => {
          setOpenedTagId(null);
     };

     const onDataChangeSnoozeTag = (primaryId, object) => {
          setSnoozeItemTags((prev) =>
               prev.map((p) => {
                    if (p.primaryId === primaryId) {
                         return {
                              ...p,
                              ...object,
                         };
                    }
                    return p;
               })
          );
     };

     const onItemUnsnooze = (primaryId) => {
          setSnoozeItemTags((prev) =>
               prev.map((p) => {
                    if (p.primaryId === primaryId) {
                         return {
                              ...p,
                              ...createEmptySnoozeTagInfo(p.primaryId ),
                         };
                    }
                    return p;
               })
          );
     };

     const onPublish = () => {
          try {
               let snoozeItems = [];
               snoozeItemTags.forEach((snoozeItemTag) => {
                    const existingSnooze = existingSnoozes.find((s) => stringUtil.areStringSame(s.primaryId, snoozeItemTag.primaryId));

                    if (existingSnooze) {
                         snoozeItems.push(snoozeItemTag);
                    } else if (snoozeItemTag.isSnoozed) {
                         snoozeItems.push(snoozeItemTag);
                    }
               });

               viewUtil.showPrompt(
                    translator.getLabel("snooze"),
                    translator.getLabel("you_are_about_to_publish_snoozed_tags"),
                    translator.getLabel("are_you_sure_you_want_to_proceed"),
                    () => {
                         addJobForSnooze(snoozeItems, "tag");
                         updateSnoozeState(snoozeItemTags, "tag");

                         stateManager.getStore().dispatch(navigationPageAction.createAction(null));
                    }
               );
          } catch (error) {
               viewUtil.showErrorAlert(error);
          }
     };

     useEffect(() => {
          const existingSnoozeTags = existingSnoozes.filter((s) => s.type === constants.SnoozeTypes.tag);

          if (checkIsDataUpdatedReverseCount.current === 0) {
               setSnoozeItemTags((prev) =>
                    prev.map((a) => {
                         const existingSnooze = existingSnoozeTags.find((snooze) => snooze.primaryId === a.primaryId);

                         if (existingSnooze) {
                              return existingSnooze;
                         } else {
                              return a;
                         }
                    })
               );
          } else {
               let snoozeItemTagsArr = [];
               const filteredTags = tags.filter((tag) => tag.type === constants.TagTypeSnooze);
               for (const snoozeTag of filteredTags) {
                    let snoozeTagInfo = existingSnoozeTags.find((a) => a.primaryId === snoozeTag.code);
                    if (snoozeTagInfo == null) {
                         snoozeTagInfo = createEmptySnoozeTagInfo(snoozeTag.code);
                    }

                    if (
                         !stringUtil.isStringNullOrEmpty(snoozeTagInfo.expiry) &&
                         snoozeTagInfo.expiry != "-1" &&
                         dateUtil.isBefore(snoozeTagInfo.expiry, dateUtil.getNow())
                    ) {
                         snoozeTagInfo = createEmptySnoozeTagInfo(snoozeTag.code);
                    }
                    snoozeItemTagsArr.push(snoozeTagInfo);
               }

               setSnoozeItemTags(snoozeItemTagsArr);
               checkIsDataUpdatedReverseCount.current = checkIsDataUpdatedReverseCount.current + 1;
          }
     }, [existingSnoozes]);

     return {
          searchText,
          setSearchText,
          snoozeItemTags,
          filteredSnoozeItemTags,
          menuOptions,
          venueOptions,
          openedTagId,
          setOpenedTagId,
          openTagModal,
          closeTagModal,
          onDataChangeSnoozeTag,
          onPublish,
          onItemUnsnooze,
     };
};

const ItemSnoozeViewContextProvider = (props) => {
     const store = useStore(props);
     return <ItemSnoozeViewContext.Provider value={store}>{props.children}</ItemSnoozeViewContext.Provider>;
};

export default ItemSnoozeViewContextProvider;

export const useSnoozeItemTags = () => useContext(ItemSnoozeViewContext).snoozeItemTags;
export const useFilteredSnoozeItemTags = () => useContext(ItemSnoozeViewContext).filteredSnoozeItemTags;
export const useOpenedTagId = () => useContext(ItemSnoozeViewContext).openedTagId;
export const useOpenTagModal = () => useContext(ItemSnoozeViewContext).openTagModal;
export const useCloseTagModal = () => useContext(ItemSnoozeViewContext).closeTagModal;
export const useSetOpenedTagId = () => useContext(ItemSnoozeViewContext).setOpenedTagId;
export const useSearchText = () => useContext(ItemSnoozeViewContext).searchText;
export const useSetSearchText = () => useContext(ItemSnoozeViewContext).setSearchText;
export const useMenuOptions = () => useContext(ItemSnoozeViewContext).menuOptions;
export const useVenueOptions = () => useContext(ItemSnoozeViewContext).venueOptions;
export const useOnDataChangeSnoozeTag = () => useContext(ItemSnoozeViewContext).onDataChangeSnoozeTag;
export const useOnPublish = () => useContext(ItemSnoozeViewContext).onPublish;
export const useOnItemUnsnooze = () => useContext(ItemSnoozeViewContext).onItemUnsnooze;

export const resetSnoozeTagInfo = () => {
     let res = {};
     res.isSnoozed = false;
     res.expiryCaption = null;
     res.expiry = null;
     res.menus = [];
     res.dispatchTypes = [];
     res.venueCodes = [];
     res.operator = null;
     return res;
};

const createEmptySnoozeTagInfo = (primaryId) => {
     return { type: constants.SnoozeTypes.tag, primaryId, ...resetSnoozeTagInfo() };
};
